import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import type { FarmState } from '../domain/model';
import { buildForm } from './schema';
import type { FormRequest, FormValues } from './types';

export interface FormDialogProps {
  request: FormRequest;
  state: FarmState;
  onCommit: (next: FarmState) => Promise<void>;
  onClose: () => void;
  notify: (message: string) => void;
}

export function FormDialog({request,state,onCommit,onClose,notify}:FormDialogProps){
  const [activeRequest,setActiveRequest]=useState(request);
  const definition=useMemo(()=>buildForm(activeRequest,state,notify),[activeRequest,state,notify]);
  const [values,setValues]=useState<FormValues>(definition.initialValues);
  const [saving,setSaving]=useState(false);
  const [error,setError]=useState('');
  const box=useRef<HTMLDivElement>(null);
  const submitting=useRef(false);
  const titleId=useId();
  useEffect(()=>{setActiveRequest(request)},[request]);
  useEffect(()=>{setValues(definition.initialValues);setError('')},[activeRequest]);
  useEffect(()=>{
    const previous=document.activeElement as HTMLElement|null;
    const previousOverflow=document.body.style.overflow;
    document.body.style.overflow='hidden';
    return()=>{document.body.style.overflow=previousOverflow;if(previous?.isConnected)previous.focus()};
  },[]);
  useEffect(()=>{
    const timer=window.setTimeout(()=>{
      const first=box.current?.querySelector<HTMLElement>('input:enabled, select:enabled, textarea:enabled, form button:enabled');
      (first||box.current)?.focus();
    },0);
    return()=>clearTimeout(timer);
  },[activeRequest]);
  const update=(id:string,value:string,reset:string[]=[])=>setValues(current=>({...current,...Object.fromEntries(reset.map(key=>[key,''])),[id]:value}));
  const submit=async(event:FormEvent)=>{
    event.preventDefault();if(submitting.current||definition.disabledReason)return;
    submitting.current=true;
    setError('');setSaving(true);
    try{
      const draft=structuredClone(state);
      const messages:string[]=[];
      const current=buildForm(activeRequest,state,message=>messages.push(message));
      if(!current.submit(values,draft)){
        setError(messages.at(-1)||'Revisa los datos del formulario.');
        return;
      }
      await onCommit(draft);
      messages.forEach(notify);
      onClose();
    }catch(cause){setError(cause instanceof Error?cause.message:'No se pudo guardar. Los datos no fueron modificados.');}
    finally{submitting.current=false;setSaving(false);}
  };
  return <div id="modal-overlay" className="open" onMouseDown={event=>{if(event.target===event.currentTarget&&!saving)onClose()}}>
    <div id="modal-box" ref={box} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-busy={saving} onKeyDown={event=>{
      if(event.key==='Escape'&&!saving){event.preventDefault();onClose();}
      if(event.key==='Tab'){
        const controls=Array.from(box.current?.querySelectorAll<HTMLElement>('button:enabled,input:enabled,select:enabled,textarea:enabled,a[href]')||[]).filter(el=>el.offsetParent!==null);
        const first=controls[0],last=controls.at(-1);
        if(!first){event.preventDefault();box.current?.focus();}
        else if(event.shiftKey&&document.activeElement===first){event.preventDefault();last?.focus();}
        else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first?.focus();}
      }
    }}>
      <button type="button" className="modal-close" aria-label="Cerrar formulario" onClick={onClose} disabled={saving}>✕</button>
      <h3 id={titleId}>{definition.title}</h3>
      {definition.description&&<p className="muted">{definition.description}</p>}
      <form onSubmit={submit}>
        <fieldset disabled={saving||!!definition.disabledReason} style={{border:0,padding:0,margin:0,minWidth:0}}>
          {definition.fields.filter(field=>!field.visible||field.visible(values)).map(field=>{
            const options=typeof field.options==='function'?field.options(values):field.options||[];
            const common={id:field.id,name:field.id,required:field.required,disabled:field.disabled,'aria-describedby':field.help?`${field.id}-help`:undefined};
            return <div className="field" key={field.id}>
              <label htmlFor={field.id}>{field.label}</label>
              {field.type==='textarea'?<textarea {...common} maxLength={field.maxLength} value={values[field.id]||''} onChange={event=>update(field.id,event.target.value)}/>
                :field.type==='select'?<select {...common} value={values[field.id]||''} onChange={event=>update(field.id,event.target.value,field.resetOnChange)}>{options.map(o=><option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}</select>
                :field.type==='multiselect'?<div id={field.id} role="group" aria-label={field.label}>{options.map(o=>{
                  let selected:string[]=[];try{selected=JSON.parse(values[field.id]||'[]')}catch{selected=[];}
                  return <label key={o.value} style={{display:'flex',gap:8,padding:'8px 0',alignItems:'center'}}><input type="checkbox" checked={selected.includes(o.value)} disabled={o.disabled} onChange={event=>update(field.id,JSON.stringify(event.target.checked?[...selected,o.value]:selected.filter(v=>v!==o.value)))}/><span>{o.label}</span></label>;
                })}</div>
                :<input {...common} type={field.type} value={values[field.id]||''} min={field.min} max={field.max} step={field.step} maxLength={field.maxLength} onChange={event=>update(field.id,event.target.value)}/>}
              {field.help&&<div className="muted" id={`${field.id}-help`}>{field.help}</div>}
            </div>;
          })}
        </fieldset>
        {(error||definition.disabledReason)&&<p role="alert" className="form-error">{error||definition.disabledReason}</p>}
        {definition.deleteRequest&&<button type="button" className="btn danger small" disabled={saving} onClick={()=>setActiveRequest(definition.deleteRequest!)}>🗑️ Eliminar este paso</button>}
        <div className="btn-row" style={{marginTop:20}}>
          <button type="button" className="btn ghost" disabled={saving} onClick={onClose}>Cancelar</button>
          <button type="submit" id="mSave" className={`btn ${definition.danger?'danger':'wheat'}`} disabled={saving||!!definition.disabledReason}>{saving?'Guardando…':definition.danger?'Confirmar eliminación':'Guardar'}</button>
        </div>
      </form>
    </div>
  </div>;
}
